import React, { useState, useMemo } from 'react';
import Icon from 'components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useToast } from 'components/Toast';

function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <p className="text-xs mt-1" style={{ color: 'var(--color-error)', fontFamily: '"IBM Plex Sans", sans-serif' }}>{msg}</p>
  );
}

// ─── Profitability Analysis Engine ───────────────────────────────────────────
function computeProfitabilityAnalysis(batches, weightToSell, pricePerTon) {
  if (!batches?.length || !weightToSell || !pricePerTon) return null;

  // Sort batches by purchase_price ascending (cheapest first = best margin first)
  const sorted = [...batches]
    ?.filter(b => (parseFloat(b?.weight_remaining) || 0) > 0.0001)
    ?.sort((a, b) => {
      const pa = parseFloat(a?.purchase_price) || 0;
      const pb = parseFloat(b?.purchase_price) || 0;
      return pa - pb;
    });

  const lotAnalysis = [];
  let accumulatedWeight = 0;
  let accumulatedMargin = 0;
  let profitableWeight = 0;
  let lossStartsAt = null;

  for (const batch of sorted) {
    const available = parseFloat(batch?.weight_remaining) || 0;
    const purchasePrice = parseFloat(batch?.purchase_price) || 0;
    const marginPerTon = pricePerTon - purchasePrice;
    const isProfitable = marginPerTon > 0;

    // How much of this batch we'd use
    const needed = Math.max(0, weightToSell - accumulatedWeight);
    const allocated = Math.min(available, needed);
    const lotMargin = allocated * marginPerTon;

    if (allocated > 0) {
      accumulatedWeight = parseFloat((accumulatedWeight + allocated)?.toFixed(6));
      accumulatedMargin = parseFloat((accumulatedMargin + lotMargin)?.toFixed(2));

      if (isProfitable) {
        profitableWeight = parseFloat((profitableWeight + allocated)?.toFixed(6));
      } else if (lossStartsAt === null) {
        lossStartsAt = parseFloat((accumulatedWeight - allocated)?.toFixed(6));
      }
    }

    lotAnalysis?.push({
      batch,
      available,
      purchasePrice,
      marginPerTon,
      isProfitable,
      allocated,
      lotMargin,
      accumulatedWeight: parseFloat(accumulatedWeight?.toFixed(6)),
      accumulatedMargin: parseFloat(accumulatedMargin?.toFixed(2)),
    });
  }

  const totalAllocated = accumulatedWeight;
  const totalMargin = accumulatedMargin;
  const allProfitable = lossStartsAt === null;
  const canFulfillFully = Math.abs(totalAllocated - weightToSell) < 0.001;

  return {
    lotAnalysis,
    profitableWeight,
    lossStartsAt,
    allProfitable,
    totalAllocated,
    totalMargin,
    canFulfillFully,
    // Batches to actually use for the sale (only allocated > 0)
    allocationPlan: lotAnalysis
      ?.filter(l => l?.allocated > 0)
      ?.map(l => ({ batch: l?.batch, allocated: l?.allocated })),
  };
}

// ─── Lot Row in Analysis Panel ────────────────────────────────────────────────
function LotRow({ lot, index, pricePerTon }) {
  const { batch, available, purchasePrice, marginPerTon, isProfitable, allocated } = lot;
  const isUsed = allocated > 0;

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors ${isUsed ? '' : 'opacity-40'}`}
      style={{
        background: isUsed
          ? isProfitable
            ? 'rgba(5,150,105,0.05)'
            : 'rgba(239,68,68,0.05)' :'var(--color-muted)',
        borderColor: isUsed
          ? isProfitable
            ? 'rgba(5,150,105,0.2)'
            : 'rgba(239,68,68,0.2)' :'var(--color-border)',
      }}
    >
      {/* Index */}
      <span
        className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
        style={{
          background: isUsed ? (isProfitable ? 'rgba(5,150,105,0.15)' : 'rgba(239,68,68,0.15)') : 'var(--color-border)',
          color: isUsed ? (isProfitable ? '#059669' : '#ef4444') : 'var(--color-muted-foreground)',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.65rem',
        }}
      >
        {index + 1}
      </span>

      {/* Batch info */}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-foreground truncate" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
          {batch?.batch_name || batch?.id?.slice(0, 8)}
        </p>
        <p className="text-xs text-muted-foreground" style={{ fontFamily: '"JetBrains Mono", monospace', fontVariantNumeric: 'tabular-nums' }}>
          {available?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 3 })} t disp.
        </p>
      </div>

      {/* Purchase price */}
      <div className="text-right flex-shrink-0">
        <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Compra</p>
        <p className="text-xs font-mono font-semibold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {purchasePrice > 0 ? `${purchasePrice?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €/t` : '—'}
        </p>
      </div>

      {/* Margin per ton */}
      <div className="text-right flex-shrink-0 w-20">
        <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Margen/t</p>
        <p
          className="text-xs font-mono font-bold"
          style={{
            fontVariantNumeric: 'tabular-nums',
            color: isProfitable ? '#059669' : '#ef4444',
          }}
        >
          {marginPerTon >= 0 ? '+' : ''}{marginPerTon?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €
        </p>
      </div>

      {/* Allocated */}
      <div className="text-right flex-shrink-0 w-16">
        <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Usar</p>
        <p
          className="text-xs font-mono font-semibold"
          style={{
            fontVariantNumeric: 'tabular-nums',
            color: isUsed ? (isProfitable ? '#059669' : '#ef4444') : 'var(--color-muted-foreground)',
          }}
        >
          {isUsed ? `${allocated?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 3 })} t` : '—'}
        </p>
      </div>
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
export default function SellFromStockModal({ group, benchmark, recoverableValue, onClose, onSuccess }) {
  const { addToast } = useToast();
  const todayStr = new Date()?.toISOString()?.split('T')?.[0];

  const avgYield = group?.avg_yield;
  const suggestedPrice = benchmark != null && avgYield != null
    ? parseFloat((benchmark * (avgYield / 100))?.toFixed(2))
    : '';

  // Phase: 'input' | 'analysis' | 'confirm_details'
  const [phase, setPhase] = useState('input');

  const [form, setForm] = useState({
    weight_to_sell: '',
    price_per_ton: suggestedPrice !== '' ? String(suggestedPrice) : '',
    buyer_name: '',
    sold_at: todayStr,
    notes: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  const maxWeight = group?.total_weight_remaining || 0;
  const weightVal = parseFloat(form?.weight_to_sell) || 0;
  const priceVal = parseFloat(form?.price_per_ton) || 0;

  // ── Profitability analysis (computed whenever weight/price change) ──────────
  const analysis = useMemo(() => {
    if (weightVal <= 0 || priceVal <= 0) return null;
    return computeProfitabilityAnalysis(group?.batches, weightVal, priceVal);
  }, [group?.batches, weightVal, priceVal]);

  // ── Validation ──────────────────────────────────────────────────────────────
  const validateField = (name, value) => {
    if (name === 'weight_to_sell') {
      const v = parseFloat(value);
      if (!value) return 'El peso a vender es obligatorio';
      if (isNaN(v) || v <= 0) return 'El peso debe ser mayor que 0';
      if (v > maxWeight) return `Máximo disponible: ${maxWeight?.toLocaleString('es-ES', { maximumFractionDigits: 3 })} t`;
    }
    if (name === 'price_per_ton') {
      const v = parseFloat(value);
      if (!value) return 'El precio de venta es obligatorio';
      if (isNaN(v) || v <= 0) return 'El precio debe ser mayor que 0';
    }
    if (name === 'buyer_name') {
      if (!value?.trim()) return 'El nombre del comprador es obligatorio';
    }
    if (name === 'sold_at') {
      if (!value) return 'La fecha de venta es obligatoria';
    }
    return null;
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      const err = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: err }));
    }
  };

  const handleBlur = (field) => {
    const err = validateField(field, form?.[field]);
    setErrors(prev => ({ ...prev, [field]: err }));
  };

  const validateInputPhase = () => {
    const fields = ['weight_to_sell', 'price_per_ton'];
    const newErrors = {};
    fields?.forEach(f => {
      const err = validateField(f, form?.[f]);
      if (err) newErrors[f] = err;
    });
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validateDetailsPhase = () => {
    const fields = ['buyer_name', 'sold_at'];
    const newErrors = {};
    fields?.forEach(f => {
      const err = validateField(f, form?.[f]);
      if (err) newErrors[f] = err;
    });
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // ── Phase transitions ────────────────────────────────────────────────────────
  const handleAnalyze = () => {
    if (!validateInputPhase()) return;
    setPhase('analysis');
  };

  const handleProceedToDetails = () => {
    setPhase('confirm_details');
  };

  const handleBackToInput = () => {
    setPhase('input');
    setErrors({});
  };

  const handleBackToAnalysis = () => {
    setPhase('analysis');
    setErrors({});
  };

  // ── Final submission ─────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!validateDetailsPhase()) return;
    if (!analysis?.allocationPlan?.length) {
      setErrors(prev => ({ ...prev, _submit: 'No hay lotes disponibles para esta operación' }));
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user?.id) throw new Error('No autenticado');

      const weightToSell = parseFloat(form?.weight_to_sell);
      const pricePerTon = parseFloat(form?.price_per_ton);
      const totalValue = weightToSell * pricePerTon;
      const soldAt = new Date(form?.sold_at)?.toISOString();

      // 1. INSERT stock_sale
      const { data: saleData, error: saleError } = await supabase
        ?.from('stock_sales')
        ?.insert({
          user_id: user?.id,
          metal_name: group?.metal_name,
          category_name: group?.category_name,
          weight_sold_t: weightToSell,
          price_per_ton: pricePerTon,
          total_value: totalValue,
          buyer_name: form?.buyer_name?.trim() || null,
          notes: form?.notes?.trim() || null,
          sold_at: soldAt,
        })
        ?.select()
        ?.single();

      if (saleError) throw saleError;

      // 2. Profitability-sorted allocation (cheapest lots first)
      const allocations = [];
      const batchUpdates = [];

      for (const { batch, allocated } of analysis?.allocationPlan) {
        const available = parseFloat(batch?.weight_remaining) || 0;
        const newWeightRemaining = parseFloat((available - allocated)?.toFixed(6));

        allocations?.push({
          stock_sale_id: saleData?.id,
          batch_id: batch?.id,
          weight_allocated: allocated,
          batch_yield_actual: batch?.yield_actual ?? batch?.yield_est ?? null,
        });

        batchUpdates?.push({
          id: batch?.id,
          weight_remaining: newWeightRemaining,
          batch_status: newWeightRemaining <= 0.0001 ? 'Completado' : batch?.batch_status,
        });
      }

      // 3. INSERT allocations
      if (allocations?.length > 0) {
        const { error: allocError } = await supabase
          ?.from('stock_sale_allocations')
          ?.insert(allocations);
        if (allocError) throw allocError;
      }

      // 4. UPDATE batch weight_remaining (and status if depleted)
      for (const upd of batchUpdates) {
        const updatePayload = { weight_remaining: upd?.weight_remaining };
        if (upd?.batch_status === 'Completado') {
          updatePayload.batch_status = 'Completado';
        }
        const { error: batchErr } = await supabase
          ?.from('batches')
          ?.update(updatePayload)
          ?.eq('id', upd?.id)
          ?.eq('user_id', user?.id);
        if (batchErr) throw batchErr;
      }

      setSavedOk(true);
      addToast(
        `Venta registrada — ${group?.category_name} ${weightToSell?.toFixed(2)}t a ${pricePerTon?.toLocaleString('es-ES', { maximumFractionDigits: 0 })}€/t`,
        'success'
      );
      setTimeout(() => {
        setSavedOk(false);
        onSuccess?.();
        onClose?.();
      }, 1000);
    } catch (err) {
      setErrors(prev => ({ ...prev, _submit: err?.message || 'Error al registrar la venta' }));
    } finally {
      setLoading(false);
    }
  };

  // ── Render helpers ───────────────────────────────────────────────────────────
  const phaseLabel = phase === 'input' ?'Paso 1 de 3 — Parámetros'
    : phase === 'analysis' ?'Paso 2 de 3 — Análisis de rentabilidad' :'Paso 3 de 3 — Confirmar venta';

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => { if (e?.target === e?.currentTarget) onClose?.(); }}
    >
      <div
        className="bg-card border border-border rounded-xl w-full max-w-xl max-h-[92vh] overflow-y-auto"
        style={{ boxShadow: 'var(--shadow-xl)' }}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-card z-10">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(5,150,105,0.1)' }}>
              <Icon name="BarChart2" size={18} color="#059669" strokeWidth={2} />
            </div>
            <div>
              <h3 className="font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}>
                Vender Stock — Análisis de Rentabilidad
              </h3>
              <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                {group?.metal_name} / {group?.category_name} · {phaseLabel}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* ── Progress bar ── */}
        <div className="flex gap-1 px-5 pt-4">
          {['input', 'analysis', 'confirm_details']?.map((p, i) => (
            <div
              key={p}
              className="h-1 flex-1 rounded-full transition-all duration-300"
              style={{
                background: phase === p
                  ? 'var(--color-accent)'
                  : (phase === 'analysis' && i === 0) || phase === 'confirm_details'
                  ? 'rgba(5,150,105,0.4)' :'var(--color-border)',
              }}
            />
          ))}
        </div>

        <div className="p-5 space-y-5">

          {/* ════════════════════════════════════════════════════════════
              PHASE 1 — Input parameters
          ════════════════════════════════════════════════════════════ */}
          {phase === 'input' && (
            <>
              {/* Reference Card */}
              <div className="rounded-lg border p-4 space-y-3" style={{ background: 'rgba(59,130,246,0.04)', borderColor: 'rgba(59,130,246,0.2)' }}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon name="Info" size={13} color="#3b82f6" strokeWidth={2} />
                  <span className="text-xs font-semibold" style={{ color: '#3b82f6', fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    Referencia de mercado
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Benchmark</p>
                    <p className="text-sm font-mono font-semibold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {benchmark != null ? `${benchmark?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €/t` : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Yield medio</p>
                    <p className="text-sm font-mono font-semibold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {avgYield != null ? `${avgYield?.toFixed(1)}%` : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Disponible total</p>
                    <p className="text-sm font-mono font-semibold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {maxWeight?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 3 })} t
                    </p>
                  </div>
                </div>
                {recoverableValue != null && (
                  <div className="pt-2 border-t" style={{ borderColor: 'rgba(59,130,246,0.15)' }}>
                    <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Valor recuperable total disponible</p>
                    <p className="text-base font-mono font-bold" style={{ color: 'var(--color-success)', fontVariantNumeric: 'tabular-nums' }}>
                      {recoverableValue?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €
                    </p>
                  </div>
                )}
              </div>

              {/* Weight + Price inputs */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    Peso a vender (t) <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="any"
                      min="0.001"
                      max={maxWeight}
                      value={form?.weight_to_sell}
                      onChange={e => handleChange('weight_to_sell', e?.target?.value)}
                      onBlur={() => handleBlur('weight_to_sell')}
                      placeholder={`Máx. ${maxWeight?.toFixed(3)}`}
                      className="w-full px-3 py-2.5 rounded-lg border text-sm bg-background text-foreground pr-8 focus:outline-none focus:ring-2"
                      style={{
                        borderColor: errors?.weight_to_sell ? 'var(--color-error)' : 'var(--color-border)',
                        fontFamily: '"JetBrains Mono", monospace',
                      }}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">t</span>
                  </div>
                  <FieldError msg={errors?.weight_to_sell} />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    Precio de venta (€/t) <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="any"
                      min="0.01"
                      value={form?.price_per_ton}
                      onChange={e => handleChange('price_per_ton', e?.target?.value)}
                      onBlur={() => handleBlur('price_per_ton')}
                      placeholder={suggestedPrice !== '' ? `Sugerido: ${suggestedPrice}` : 'Precio €/t'}
                      className="w-full px-3 py-2.5 rounded-lg border text-sm bg-background text-foreground pr-10 focus:outline-none focus:ring-2"
                      style={{
                        borderColor: errors?.price_per_ton ? 'var(--color-error)' : 'var(--color-border)',
                        fontFamily: '"JetBrains Mono", monospace',
                      }}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€/t</span>
                  </div>
                  {suggestedPrice !== '' && !form?.price_per_ton && (
                    <button
                      type="button"
                      onClick={() => handleChange('price_per_ton', String(suggestedPrice))}
                      className="text-xs mt-1 hover:underline"
                      style={{ color: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }}
                    >
                      Usar precio sugerido: {suggestedPrice?.toLocaleString('es-ES')} €/t
                    </button>
                  )}
                  <FieldError msg={errors?.price_per_ton} />
                </div>
              </div>

              {/* Quick preview while typing */}
              {analysis && (
                <div className="rounded-lg border p-3 flex items-center gap-3" style={{ background: 'var(--color-muted)', borderColor: 'var(--color-border)' }}>
                  <Icon name="Zap" size={14} color="var(--color-accent)" strokeWidth={2} />
                  <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    Análisis listo —{' '}
                    <span style={{ color: analysis?.allProfitable ? '#059669' : '#f59e0b', fontWeight: 600 }}>
                      {analysis?.allProfitable
                        ? `${analysis?.totalAllocated?.toLocaleString('es-ES', { maximumFractionDigits: 2 })} t con margen positivo`
                        : `${analysis?.profitableWeight?.toLocaleString('es-ES', { maximumFractionDigits: 2 })} t rentables de ${weightVal?.toLocaleString('es-ES', { maximumFractionDigits: 2 })} t solicitadas`
                      }
                    </span>
                    {' '}· Continúa para ver el detalle.
                  </p>
                </div>
              )}
            </>
          )}

          {/* ════════════════════════════════════════════════════════════
              PHASE 2 — Profitability analysis
          ════════════════════════════════════════════════════════════ */}
          {phase === 'analysis' && analysis && (
            <>
              {/* Summary KPIs */}
              <div className="grid grid-cols-2 gap-3">
                {/* Profitable tons */}
                <div className="rounded-lg border p-3" style={{ background: 'rgba(5,150,105,0.05)', borderColor: 'rgba(5,150,105,0.2)' }}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon name="TrendingUp" size={12} color="#059669" strokeWidth={2} />
                    <p className="text-xs font-semibold" style={{ color: '#059669', fontFamily: '"IBM Plex Sans", sans-serif' }}>Toneladas rentables</p>
                  </div>
                  <p className="text-lg font-mono font-bold" style={{ color: '#059669', fontVariantNumeric: 'tabular-nums' }}>
                    {analysis?.profitableWeight?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 3 })} t
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    de {weightVal?.toLocaleString('es-ES', { maximumFractionDigits: 2 })} t solicitadas
                  </p>
                </div>

                {/* Total margin */}
                <div
                  className="rounded-lg border p-3"
                  style={{
                    background: analysis?.totalMargin >= 0 ? 'rgba(5,150,105,0.05)' : 'rgba(239,68,68,0.05)',
                    borderColor: analysis?.totalMargin >= 0 ? 'rgba(5,150,105,0.2)' : 'rgba(239,68,68,0.2)',
                  }}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon name="Euro" size={12} color={analysis?.totalMargin >= 0 ? '#059669' : '#ef4444'} strokeWidth={2} />
                    <p className="text-xs font-semibold" style={{ color: analysis?.totalMargin >= 0 ? '#059669' : '#ef4444', fontFamily: '"IBM Plex Sans", sans-serif' }}>
                      Margen total esperado
                    </p>
                  </div>
                  <p
                    className="text-lg font-mono font-bold"
                    style={{ color: analysis?.totalMargin >= 0 ? '#059669' : '#ef4444', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {analysis?.totalMargin >= 0 ? '+' : ''}{analysis?.totalMargin?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    sobre {analysis?.totalAllocated?.toLocaleString('es-ES', { maximumFractionDigits: 2 })} t asignadas
                  </p>
                </div>
              </div>

              {/* Loss threshold warning */}
              {!analysis?.allProfitable && analysis?.lossStartsAt !== null && (
                <div className="flex items-start gap-2.5 p-3 rounded-lg border" style={{ background: 'rgba(245,158,11,0.07)', borderColor: 'rgba(245,158,11,0.25)' }}>
                  <Icon name="AlertTriangle" size={15} color="#f59e0b" strokeWidth={2} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold" style={{ color: '#f59e0b', fontFamily: '"IBM Plex Sans", sans-serif' }}>
                      Pérdida a partir de {analysis?.lossStartsAt?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 3 })} t
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                      Las primeras {analysis?.profitableWeight?.toLocaleString('es-ES', { maximumFractionDigits: 2 })} t generan margen positivo.
                      El volumen restante se vendería por debajo del precio de compra.
                    </p>
                  </div>
                </div>
              )}

              {/* All profitable banner */}
              {analysis?.allProfitable && (
                <div className="flex items-center gap-2.5 p-3 rounded-lg border" style={{ background: 'rgba(5,150,105,0.07)', borderColor: 'rgba(5,150,105,0.25)' }}>
                  <Icon name="CheckCircle" size={15} color="#059669" strokeWidth={2} />
                  <p className="text-xs font-semibold" style={{ color: '#059669', fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    Toda la operación genera margen positivo al precio propuesto
                  </p>
                </div>
              )}

              {/* Lot-by-lot breakdown */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-foreground uppercase tracking-wide" style={{ fontFamily: '"IBM Plex Sans", sans-serif', letterSpacing: '0.05em' }}>
                    Desglose por lote (ordenado por precio de compra ↑)
                  </p>
                  <span className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    {analysis?.lotAnalysis?.filter(l => l?.allocated > 0)?.length} lote{analysis?.lotAnalysis?.filter(l => l?.allocated > 0)?.length !== 1 ? 's' : ''} usados
                  </span>
                </div>
                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  {analysis?.lotAnalysis?.map((lot, i) => (
                    <LotRow key={lot?.batch?.id} lot={lot} index={i} pricePerTon={priceVal} />
                  ))}
                </div>
              </div>

              {/* Params summary */}
              <div className="rounded-lg border border-border p-3 flex items-center justify-between" style={{ background: 'var(--color-muted)' }}>
                <div className="flex items-center gap-2">
                  <Icon name="Package" size={13} color="var(--color-muted-foreground)" strokeWidth={2} />
                  <span className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    {weightVal?.toLocaleString('es-ES', { maximumFractionDigits: 2 })} t × {priceVal?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €/t
                  </span>
                </div>
                <span className="text-sm font-mono font-bold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {(weightVal * priceVal)?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €
                </span>
              </div>
            </>
          )}

          {/* ════════════════════════════════════════════════════════════
              PHASE 3 — Sale details + confirm
          ════════════════════════════════════════════════════════════ */}
          {phase === 'confirm_details' && (
            <>
              {/* Compact analysis recap */}
              <div className="rounded-lg border p-3 space-y-2" style={{ background: 'rgba(5,150,105,0.05)', borderColor: 'rgba(5,150,105,0.2)' }}>
                <p className="text-xs font-semibold" style={{ color: '#059669', fontFamily: '"IBM Plex Sans", sans-serif' }}>Resumen del análisis</p>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Peso</p>
                    <p className="text-sm font-mono font-bold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {weightVal?.toLocaleString('es-ES', { maximumFractionDigits: 2 })} t
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Precio</p>
                    <p className="text-sm font-mono font-bold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {priceVal?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €/t
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Margen</p>
                    <p
                      className="text-sm font-mono font-bold"
                      style={{
                        fontVariantNumeric: 'tabular-nums',
                        color: analysis?.totalMargin >= 0 ? '#059669' : '#ef4444',
                      }}
                    >
                      {analysis?.totalMargin >= 0 ? '+' : ''}{analysis?.totalMargin?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €
                    </p>
                  </div>
                </div>
                {!analysis?.allProfitable && (
                  <p className="text-xs" style={{ color: '#f59e0b', fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    ⚠ Parte del volumen se vende con pérdida (a partir de {analysis?.lossStartsAt?.toLocaleString('es-ES', { maximumFractionDigits: 2 })} t)
                  </p>
                )}
              </div>

              {/* Buyer + Date + Notes */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    Comprador <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={form?.buyer_name}
                    onChange={e => handleChange('buyer_name', e?.target?.value)}
                    onBlur={() => handleBlur('buyer_name')}
                    placeholder="Nombre del comprador"
                    className="w-full px-3 py-2.5 rounded-lg border text-sm bg-background text-foreground focus:outline-none focus:ring-2"
                    style={{
                      borderColor: errors?.buyer_name ? 'var(--color-error)' : 'var(--color-border)',
                      fontFamily: '"IBM Plex Sans", sans-serif',
                    }}
                  />
                  <FieldError msg={errors?.buyer_name} />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    Fecha de venta <span style={{ color: 'var(--color-error)' }}>*</span>
                  </label>
                  <input
                    type="date"
                    value={form?.sold_at}
                    onChange={e => handleChange('sold_at', e?.target?.value)}
                    onBlur={() => handleBlur('sold_at')}
                    max={todayStr}
                    className="w-full px-3 py-2.5 rounded-lg border text-sm bg-background text-foreground focus:outline-none focus:ring-2"
                    style={{
                      borderColor: errors?.sold_at ? 'var(--color-error)' : 'var(--color-border)',
                      fontFamily: '"IBM Plex Sans", sans-serif',
                    }}
                  />
                  <FieldError msg={errors?.sold_at} />
                </div>

                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-foreground mb-1.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                    Notas (opcional)
                  </label>
                  <textarea
                    value={form?.notes}
                    onChange={e => handleChange('notes', e?.target?.value)}
                    placeholder="Observaciones sobre la venta..."
                    rows={2}
                    className="w-full px-3 py-2.5 rounded-lg border text-sm bg-background text-foreground focus:outline-none focus:ring-2 resize-none"
                    style={{ borderColor: 'var(--color-border)', fontFamily: '"IBM Plex Sans", sans-serif' }}
                  />
                </div>
              </div>

              {/* Allocation method note */}
              <div className="flex items-center gap-1.5 px-1">
                <Icon name="ArrowDownUp" size={11} color="var(--color-muted-foreground)" strokeWidth={2} />
                <span className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                  Asignación por rentabilidad — lotes más baratos primero ({analysis?.allocationPlan?.length} lote{analysis?.allocationPlan?.length !== 1 ? 's' : ''})
                </span>
              </div>

              {/* Submit error */}
              {errors?._submit && (
                <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
                  <Icon name="AlertCircle" size={14} color="var(--color-error)" strokeWidth={2} />
                  <p className="text-sm" style={{ color: 'var(--color-error)', fontFamily: '"IBM Plex Sans", sans-serif' }}>{errors?._submit}</p>
                </div>
              )}

              {/* Success */}
              {savedOk && (
                <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.2)' }}>
                  <Icon name="CheckCircle" size={14} color="var(--color-success)" strokeWidth={2} />
                  <p className="text-sm font-medium" style={{ color: 'var(--color-success)', fontFamily: '"IBM Plex Sans", sans-serif' }}>Venta registrada correctamente</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-border sticky bottom-0 bg-card">
          {/* Left: back / cancel */}
          <div>
            {phase === 'input' && (
              <button
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
              >
                Cancelar
              </button>
            )}
            {phase === 'analysis' && (
              <button
                onClick={handleBackToInput}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
              >
                <Icon name="ChevronLeft" size={14} color="var(--color-muted-foreground)" strokeWidth={2} />
                Modificar parámetros
              </button>
            )}
            {phase === 'confirm_details' && (
              <button
                onClick={handleBackToAnalysis}
                disabled={loading}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
              >
                <Icon name="ChevronLeft" size={14} color="var(--color-muted-foreground)" strokeWidth={2} />
                Ver análisis
              </button>
            )}
          </div>

          {/* Right: primary action */}
          <div>
            {phase === 'input' && (
              <button
                onClick={handleAnalyze}
                className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95"
                style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }}
              >
                <Icon name="BarChart2" size={15} color="#fff" strokeWidth={2.5} />
                Analizar rentabilidad
              </button>
            )}
            {phase === 'analysis' && (
              <button
                onClick={handleProceedToDetails}
                className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95"
                style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }}
              >
                Continuar con la venta
                <Icon name="ChevronRight" size={15} color="#fff" strokeWidth={2.5} />
              </button>
            )}
            {phase === 'confirm_details' && (
              <button
                onClick={handleConfirm}
                disabled={loading || savedOk}
                className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-95 disabled:opacity-50"
                style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }}
              >
                {loading ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <Icon name="CheckCircle" size={15} color="#fff" strokeWidth={2.5} />
                    Confirmar Venta
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
